import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CourseWithUnits, DetailedLesson, ExamSummary, Lesson } from '../../types';
import { api } from '../../services/api';

type NextCourseItem =
  | { type: 'lesson'; data: Lesson; path: string }
  | { type: 'exam'; data: ExamSummary; path: string };

const makeChatId = () => Math.random().toString(36).substr(2, 9);

function getNextCourseItem(
  course: CourseWithUnits | null,
  lessonId: string | undefined
): NextCourseItem | null {
  if (!course || !lessonId) return null;

  const items = (course.units || [])
    .slice()
    .sort((a, b) => a.order_index - b.order_index)
    .flatMap(unit => [
      ...unit.lessons.map(lesson => ({
        type: 'lesson' as const,
        orderIndex: lesson.order_index ?? 0,
        name: lesson.title,
        data: lesson
      })),
      ...(unit.exams || []).map(exam => ({
        type: 'exam' as const,
        orderIndex: exam.order_index,
        name: exam.name,
        data: exam
      }))
    ].sort((a, b) => {
      if (a.orderIndex !== b.orderIndex) return a.orderIndex - b.orderIndex;
      return a.name.localeCompare(b.name);
    }));

  const currentIndex = items.findIndex(item => item.type === 'lesson' && item.data.id === lessonId);
  const nextItem = currentIndex >= 0 ? items[currentIndex + 1] : null;
  if (!nextItem) return null;

  if (nextItem.type === 'lesson') {
    return {
      type: 'lesson',
      data: nextItem.data,
      path: `/lesson/${encodeURIComponent(nextItem.data.id)}/${makeChatId()}`
    };
  }

  return {
    type: 'exam',
    data: nextItem.data,
    path: `/exam/${encodeURIComponent(nextItem.data.id)}`
  };
}

interface LessonCompletionModalProps {
  open: boolean;
  lesson: DetailedLesson;
  course: CourseWithUnits | null;
  chatId: string;
  onClose: () => void;
}

function LessonCompletionModal({ open, lesson, course, chatId, onClose }: LessonCompletionModalProps) {
  const [rating, setRating] = useState<'up' | 'down' | null>(null);
  const [ratingStatus, setRatingStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const nextCourseItem = useMemo(
    () => getNextCourseItem(course, lesson.id),
    [course, lesson.id]
  );

  useEffect(() => {
    setRating(null);
    setRatingStatus('idle');
  }, [chatId]);

  const handleRating = async (nextRating: 'up' | 'down') => {
    if (!chatId || ratingStatus === 'submitting') return;
    setRatingStatus('submitting');
    try {
      await api.rateLessonChat(chatId, nextRating);
      setRating(nextRating);
      setRatingStatus('idle');
    } catch {
      setRatingStatus('error');
    }
  };

  if (!open) return null;

  return (
    <div
      className="lesson-completion-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="lesson-completion-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lesson-completion-title"
      >
        <button
          type="button"
          className="lesson-completion-modal-close"
          onClick={onClose}
          aria-label="Close lesson completion modal"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        <div className="lesson-completion-modal-hero">
          <span className="lesson-completion-modal-eyebrow">Lesson complete</span>
          <h2 id="lesson-completion-title">{lesson.title}</h2>
        </div>

        <div className="lesson-completion-modal-body">
          <p className="lesson-completion-modal-copy">
            Nice work! You can keep chatting about this lesson here, or jump to what&rsquo;s next when you&rsquo;re ready.
          </p>

          <div className="lesson-completion-modal-rating">
            <span>How was this lesson?</span>
            <div className="lesson-completion-modal-rating-buttons">
              <button
                type="button"
                className={rating === 'up' ? 'is-selected' : ''}
                aria-label="Rate lesson thumbs up"
                aria-pressed={rating === 'up'}
                disabled={ratingStatus === 'submitting'}
                onClick={() => void handleRating('up')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7 10v12" />
                  <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
                </svg>
              </button>
              <button
                type="button"
                className={rating === 'down' ? 'is-selected' : ''}
                aria-label="Rate lesson thumbs down"
                aria-pressed={rating === 'down'}
                disabled={ratingStatus === 'submitting'}
                onClick={() => void handleRating('down')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M17 14V2" />
                  <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z" />
                </svg>
              </button>
            </div>
            {ratingStatus === 'error' && (
              <span className="lesson-completion-modal-rating-error" role="status">
                Could not save your rating. Please try again.
              </span>
            )}
          </div>

          {nextCourseItem ? (
            <div className="lesson-completion-modal-next">
              <span className="lesson-completion-modal-section-label">Up next</span>
              <Link
                to={nextCourseItem.path}
                className={`lesson-completion-modal-next-card${nextCourseItem.type === 'exam' ? ' lesson-completion-modal-next-card--exam' : ''}`}
                onClick={onClose}
              >
                <span className="lesson-completion-modal-next-content">
                  <span
                    className={`lesson-completion-modal-next-kind${nextCourseItem.type === 'exam' ? ' lesson-completion-modal-next-kind--exam' : ''}`}
                  >
                    {nextCourseItem.type === 'exam' ? 'Exam' : 'Lesson'}
                  </span>
                  <span className="lesson-completion-modal-next-title">
                    {nextCourseItem.type === 'exam' ? nextCourseItem.data.name : nextCourseItem.data.title}
                  </span>
                </span>
                <span className="lesson-completion-modal-next-arrow" aria-hidden="true">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </span>
              </Link>
            </div>
          ) : (
            <div className="lesson-completion-modal-finished">
              <p>You&rsquo;ve completed every lesson and exercise in this course. Amazing work!</p>
            </div>
          )}

          <div className="lesson-completion-modal-actions">
            {course && (
              <Link
                to={`/course/${course.id}`}
                className="lesson-completion-modal-secondary"
                onClick={onClose}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m12 19-7-7 7-7" />
                  <path d="M19 12H5" />
                </svg>
                Course overview
              </Link>
            )}
            <button
              type="button"
              className="lesson-completion-modal-tertiary"
              onClick={onClose}
            >
              Stay here
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LessonCompletionModal;
